import { useState, useCallback, useEffect } from 'react';
import { fetchApi } from '../utils/api';

// List of all storage keys in the application
export enum LocalKeys {
  HOME_LINKS = '@HOME_LINKS',
  // STATEMENT_OVERVIEW removed - now fetched directly from API
  // MEMBER_STOCK_STATEMENT removed - now fetched directly from API
  // MEMBER_STOCK_STATEMENT_RISKY removed - now fetched directly from API
  PROFILE_DATA = '@PROFILE_DATA',
  TELEGRAM_ID = '@TELEGRAM_ID',
  HISTORY_ACTIVE_TAB = '@HISTORY_ACTIVE_TAB',
  // BANK_ACCOUNTS removed - now fetched directly from API
  // MONEY_DEPOSIT_LIST removed - now fetched directly from API
  // BANK_LIST removed - now fetched directly from API
  // DISPLAY_BORROW_STOCK removed - now fetched directly from API
  ORDER_SELECTED_STOCK = '@ORDER_SELECTED_STOCK',
  SUB_ACCOUNT_LIST = '@SUB_ACCOUNT_LIST',
}

// Type for the hook return value
export type LocalDataResult<T> = {
  data: T | null;
  setData: (newData: T) => void;
  refetch: () => Promise<void>;
  refreshFromStorage: () => boolean;
};

/**
 * Debug mode flag - set to true to enable detailed logging
 * Set to true when troubleshooting localStorage issues
 */
const DEBUG_LOCAL = true; // Set to true temporarily when troubleshooting

/**
 * Helper function to log debug messages
 */
const debugLog = (message: string, ...data: any[]) => {
  if (DEBUG_LOCAL) {
    console.log(`[LocalData] ${message}`, ...data);
  }
};

/**
 * Helper function to log debug errors
 */
const debugError = (message: string, ...data: any[]) => {
  if (DEBUG_LOCAL) {
    console.error(`[LocalData] ${message}`, ...data);
  }
};

/**
 * Custom hook to access localStorage data with automatic JSON parsing
 * 
 * @param key - The local storage key to retrieve
 * @param defaultValue - Optional default value if the key doesn't exist
 * @returns The parsed value from local storage and status information
 * 
 * @example
 * // Get profile data
 * const { data: profileData, loading, error, setData } = useLocalData(LocalKeys.PROFILE_DATA);
 * 
 * // With type casting
 * const { data: homeLinks } = useLocalData<{ faq: string, manual: string }>(LocalKeys.HOME_LINKS);
 * 
 * // With default value
 * const { data: statement } = useLocalData(LocalKeys.STATEMENT_OVERVIEW, { value: 0 });
 *
 * // Update data
 * const { data: profileData, setData: updateProfile } = useLocalData(LocalKeys.PROFILE_DATA);
 * updateProfile({ ...profileData, name: 'New Name' });
 */
// API endpoints configuration that matches auth.service.ts loadInitialData method
const API_ENDPOINTS_MAP: Record<string, { method: 'get' | 'post', url: string, defaultValue: any }> = {
  // STATEMENT_OVERVIEW removed - now fetched directly from API
  [LocalKeys.PROFILE_DATA]: { method: 'get', url: 'api/Account/GetProfile', defaultValue: {} },
  [LocalKeys.TELEGRAM_ID]: { method: 'get', url: 'api/Account/GetTelegramId', defaultValue: '' },
  // BANK_ACCOUNTS removed - now fetched directly from API
  // BANK_LIST removed - now fetched directly from API
  // DISPLAY_BORROW_STOCK removed - now fetched directly from API
};

// Data processors for each type of data, matching exactly the implementation in auth.service.ts
const API_DATA_PROCESSORS: Record<string, (data: any) => any> = {
  // STATEMENT_OVERVIEW removed - now fetched directly from API
  
  // DISPLAY_BORROW_STOCK removed - now fetched directly from API
  
  // Profile Data - Log and return data
  [LocalKeys.PROFILE_DATA]: (data: any) => {
    console.log('useLocalData: refetch storing profile data');
    return data;
  },
  
  // Telegram ID - Extract Message field if Status is Success
  [LocalKeys.TELEGRAM_ID]: (data: any) => {
    if (data && data.Status === 'Success') {
      return data.Message;
    }
    return '';
  },
  
  // Bank Accounts and Bank List data processors have been removed
  // These are now fetched directly from the API
};

export function useLocalData<T = any>(
  key: LocalKeys | string,
  defaultValue: T | null = null
): LocalDataResult<T> {
  // Convert key to string to avoid enum comparison issues
  const keyString = key.toString();
  
  // Initialize state with data from localStorage or default value
  const getInitialData = (): T | null => {
    try {
      const item = localStorage.getItem(keyString);
      debugLog(`Reading from local storage: ${keyString} =>`, item ? (item.length > 100 ? item.substring(0, 100) + '...' : item) : 'null');
      
      if (item) {
        try {
          const parsedData = JSON.parse(item);
          debugLog(`Parsed data for ${keyString}:`, parsedData);
          return parsedData;
        } catch (parseError) {
          debugError(`Error parsing data for key ${keyString}:`, parseError);
          console.error(`Failed to parse data for ${keyString}:`, item);
          return defaultValue;
        }
      } else {
        // No data found for this key
        debugLog(`No data found for key ${keyString}, using default:`, defaultValue);
        return defaultValue;
      }
    } catch (e) {
      debugError(`Error accessing local storage for key ${keyString}:`, e);
      return defaultValue;
    }
  };

  const [data, setStateData] = useState<T | null>(() => {
    // Add an extra log when initializing with PROFILE_DATA to help debug
    if (keyString === LocalKeys.PROFILE_DATA) {
      console.log(`[LocalData] Initializing ${keyString} hook, raw data:`, localStorage.getItem(keyString));
    }
    return getInitialData();
  });
  
  // Manually refresh data from localStorage
  const refreshFromStorage = useCallback(() => {
    try {
      const refreshedData = getInitialData();
      if (refreshedData !== null) {
        setStateData(refreshedData);
        console.log(`[LocalData] Manually refreshed ${keyString} from localStorage:`, 
          typeof refreshedData === 'object' ? 'Object data (see debug logs)' : refreshedData);
        return true;
      }
    } catch (e) {
      console.error(`[LocalData] Error refreshing ${keyString} from localStorage:`, e);
    }
    return false;
  }, [keyString]);
  
  // Listen for storage events (changes from other tabs/windows)
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === keyString) {
        console.log(`[LocalData] Storage event detected for ${keyString}`);
        
        if (event.newValue !== null) {
          try {
            const newData = JSON.parse(event.newValue) as T;
            console.log(`[LocalData] Updating ${keyString} from storage event`);
            setStateData(newData);
          } catch (parseError) {
            console.error(`[LocalData] Error parsing ${keyString} from storage event:`, parseError);
          }
        } else {
          // Key was removed
          console.log(`[LocalData] ${keyString} was removed from localStorage`);
          setStateData(defaultValue);
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [keyString, defaultValue]);

  // Function to update data in both state and local storage
  const setData = (newData: T) => {
    try {
      // Update state
      setStateData(newData);
      
      // Update local storage
      const jsonData = JSON.stringify(newData);
      localStorage.setItem(keyString, jsonData);
      debugLog(`Updated data for key ${keyString}:`, newData);
    } catch (e) {
      const err = e instanceof Error ? e : new Error('Unknown error saving local data');
      debugError(`Error saving local data for key ${keyString}:`, err);
    }
  };

  // Function to fetch data from API based on key
  const refetch = useCallback(async () => {
    try {
      console.log(`[LocalData] Refetching data for ${keyString} from API...`);
      
      // Get endpoint configuration
      const endpoint = API_ENDPOINTS_MAP[keyString];
      if (!endpoint) {
        console.warn(`[LocalData] No API endpoint configured for key: ${keyString}`);
        return;
      }
      
      // Call the API
      let result;
      try {
        if (endpoint.method === 'post') {
          result = await fetchApi('post', endpoint.url, {}, 'json', { skipCache: true });
        } else {
          result = await fetchApi('get', endpoint.url, {}, 'json', { skipCache: true });
        }
      } catch (apiError) {
        console.error(`[LocalData] API error when fetching ${keyString}:`, apiError);
        result = endpoint.defaultValue;
      }
      
      // Process data with the appropriate processor
      const processor = API_DATA_PROCESSORS[keyString];
      if (!processor) {
        console.warn(`[LocalData] No processor defined for key: ${keyString}, using raw data`);
      }
      
      const processedData = processor ? processor(result) : result;
      
      // Only proceed if we have valid data
      if (processedData !== undefined && processedData !== null) {
        // Update state
        setStateData(processedData as T);
        
        // Log data before storing (abbreviated for large objects)
        console.log(`[LocalData] Storing ${keyString} in storage:`, 
          typeof processedData === 'object' 
            ? JSON.stringify(processedData).substring(0, 100) + '...' 
            : processedData);
        
        // Store in localStorage
        localStorage.setItem(keyString, JSON.stringify(processedData));
        
        // Verify the data was stored
        const dataExists = localStorage.getItem(keyString) !== null;
        console.log(`[LocalData] Verification: ${keyString} exists in storage: ${dataExists}`);
        
        if (!dataExists) {
          console.error(`[LocalData] Failed to store ${keyString}`);
        } else {
          console.log(`[LocalData] Successfully refetched and stored data for ${keyString}`);
        }
      } else {
        console.warn(`[LocalData] Processed data for ${keyString} is null or undefined, not storing`);
      }
    } catch (error) {
      console.error(`[LocalData] Unhandled error during refetch for ${keyString}:`, error);
    }
  }, [keyString]);

  return { data, setData, refetch, refreshFromStorage };
}

/**
 * Simplified hook to directly get localStorage data without loading/error states
 * Useful for cases where you just need the data and don't care about loading state
 * 
 * @param key - The local storage key to retrieve
 * @param defaultValue - Optional default value if the key doesn't exist
 * @returns Just the parsed data
 * 
 * @example
 * // Get profile data
 * const profileData = useLocalValue(LocalKeys.PROFILE_DATA);
 */
export function useLocalValue<T = any>(
  key: LocalKeys | string,
  defaultValue: T | null = null
): T | null {
  const { data } = useLocalData<T>(key, defaultValue);
  return data;
}

/**
 * Utility function to get data from localStorage without using a hook
 * Useful for non-component code that needs to access localStorage data
 * 
 * @param key - The local storage key to retrieve
 * @param defaultValue - Optional default value if the key doesn't exist
 * @returns The parsed value or default value
 * 
 * @example
 * // Get profile data
 * const profileData = getLocalData(LocalKeys.PROFILE_DATA);
 */
export function getLocalData<T = any>(
  key: LocalKeys | string,
  defaultValue: T | null = null
): T | null {
  const keyString = key.toString();
  try {
    const item = localStorage.getItem(keyString);
    if (!item) return defaultValue;
    
    try {
      return JSON.parse(item);
    } catch (e) {
      debugError(`Error parsing data for key ${keyString}:`, e);
      return defaultValue;
    }
  } catch (e) {
    debugError(`Error accessing local storage for key ${keyString}:`, e);
    return defaultValue;
  }
}

/**
 * Utility function to set data in localStorage without using a hook
 * Useful for non-component code that needs to update localStorage data
 * 
 * @param key - The local storage key to update
 * @param value - The value to store (will be JSON stringified)
 * @returns true if successful, false if there was an error
 * 
 * @example
 * // Set profile data
 * setLocalData(LocalKeys.PROFILE_DATA, { name: 'New Name' });
 */
export function setLocalData<T = any>(
  key: LocalKeys | string,
  value: T
): boolean {
  const keyString = key.toString();
  try {
    const jsonData = JSON.stringify(value);
    localStorage.setItem(keyString, jsonData);
    debugLog(`Set data for key ${keyString}:`, value);
    return true;
  } catch (e) {
    debugError(`Error setting data for key ${keyString}:`, e);
    return false;
  }
}

/**
 * Utility function to remove data from localStorage
 * 
 * @param key - The local storage key to remove
 * @returns true if successful, false if there was an error
 * 
 * @example
 * // Remove profile data
 * removeLocalData(LocalKeys.PROFILE_DATA);
 */
export function removeLocalData(
  key: LocalKeys | string
): boolean {
  const keyString = key.toString();
  try {
    localStorage.removeItem(keyString);
    debugLog(`Removed data for key ${keyString}`);
    return true;
  } catch (e) {
    debugError(`Error removing data for key ${keyString}:`, e);
    return false;
  }
}

/**
 * Utility function to check if data exists in localStorage
 * 
 * @param key - The local storage key to check
 * @returns true if the key exists, false otherwise
 * 
 * @example
 * // Check if profile data exists
 * if (hasLocalData(LocalKeys.PROFILE_DATA)) {
 *   // Do something
 * }
 */
export function hasLocalData(
  key: LocalKeys | string
): boolean {
  const keyString = key.toString();
  try {
    return localStorage.getItem(keyString) !== null;
  } catch (e) {
    debugError(`Error checking data for key ${keyString}:`, e);
    return false;
  }
}

/**
 * @deprecated Use getLocalData instead
 */
export const getSessionData = getLocalData;

/**
 * @deprecated Use setLocalData instead
 */
export const setSessionData = setLocalData;

/**
 * @deprecated Use removeLocalData instead
 */
export const removeSessionData = removeLocalData;

/**
 * @deprecated Use hasLocalData instead
 */
export const hasSessionData = hasLocalData;

/**
 * @deprecated Use LocalKeys instead
 */
export const SessionKeys = LocalKeys;
